import React, { useState, useEffect } from 'react';
import { Select, Button, Input, Spin, message } from 'antd';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getEndpoint } from '../utils/endpoints';

const { TextArea } = Input;

interface OptionItem {
  value: string;
  label: string;
}

interface ClientRecord {
  client_code: string;
  client_name?: string;
  preferences?: Record<string, Record<string, unknown>>;
}

interface PreferencesResponse {
  used_glossaries?: string[];
  used_memories?: string[];
  used_guidelines?: string[];
}

const normalizeString = (value?: string | null) => (value || '').trim().toLowerCase();

const createOptions = (
  items: any[] | undefined,
  valueKey: string,
  labelKey: string,
  fallbackLabelTransform?: (value: string) => string
): OptionItem[] => {
  if (!items) {
    return [];
  }
  const seen = new Set<string>();
  const options: OptionItem[] = [];

  items.forEach((item) => {
    if (!item) return;
    const rawValue = item[valueKey] ?? item[labelKey];
    if (!rawValue) return;

    const value = normalizeString(String(rawValue));
    if (!value || seen.has(value)) return;

    seen.add(value);

    const rawLabel = item[labelKey] ?? item[valueKey] ?? value;
    const label =
      typeof rawLabel === 'string'
        ? rawLabel
        : fallbackLabelTransform
        ? fallbackLabelTransform(value)
        : value.toUpperCase();

    options.push({ value, label });
  });

  return options;
};

const normalizeValueList = (values: unknown): string[] => {
  if (!values) return [];
  const list = Array.isArray(values) ? values : [values];
  const normalized = list
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter((item) => item && item !== '-')
    .map((item) => item.toLowerCase());
  return Array.from(new Set(normalized));
};

const TestTranslation: React.FC = () => {
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [clientsLoading, setClientsLoading] = useState<boolean>(false);
  const [selectedClient, setSelectedClient] = useState<string | undefined>();
  const [directionOptions, setDirectionOptions] = useState<OptionItem[]>([]);
  const [directionDomainMap, setDirectionDomainMap] = useState<Record<string, string>>({});
  const [selectedDirection, setSelectedDirection] = useState<string | undefined>();
  const [selectedDomain, setSelectedDomain] = useState<string | undefined>();

  const [glossaryOptions, setGlossaryOptions] = useState<OptionItem[]>([]);
  const [memoryOptions, setMemoryOptions] = useState<OptionItem[]>([]);
  const [guidelineOptions, setGuidelineOptions] = useState<OptionItem[]>([]);

  const [selectedGlossaries, setSelectedGlossaries] = useState<string[]>([]);
  const [selectedMemories, setSelectedMemories] = useState<string[]>([]);
  const [selectedGuidelines, setSelectedGuidelines] = useState<string[]>([]);
  const [selectedSpeed, setSelectedSpeed] = useState<string>('medium');

  const [additionalInstructions, setAdditionalInstructions] = useState<string>('');
  const [textToTranslate, setTextToTranslate] = useState<string>('');
  const [translatedText, setTranslatedText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [preferencesLoading, setPreferencesLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      setClientsLoading(true);
      try {
        const [clientsRes, glossariesRes, memoriesRes, guidelinesRes] = await Promise.all([
          axios.get(`${getEndpoint('BACKEND')}/getClients/`),
          axios.get(`${getEndpoint('BACKEND')}/getGlossaries/`),
          axios.get(`${getEndpoint('BACKEND')}/getMemories/`),
          axios.get(`${getEndpoint('BACKEND')}/getGuidelines/`),
        ]);

        setClients(clientsRes.data || []);

        setGlossaryOptions(
          createOptions(glossariesRes.data, 'glossary_code', 'glossary_name')
        );
        setMemoryOptions(createOptions(memoriesRes.data, 'memory_code', 'memory_name'));
        setGuidelineOptions(
          createOptions(guidelinesRes.data, 'guideline_code', 'guideline_name')
        );
      } catch (error) {
        console.error('Error fetching initial data:', error);
        message.error('Failed to load initial data.');
      } finally {
        setClientsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const ensureGlossaryOption = (code: string) => {
    if (!code) return;
    setGlossaryOptions((prev) =>
      prev.some((option) => option.value === code)
        ? prev
        : [...prev, { value: code, label: code.toUpperCase() }]
    );
  };

  const ensureMemoryOption = (code: string) => {
    if (!code) return;
    setMemoryOptions((prev) =>
      prev.some((option) => option.value === code)
        ? prev
        : [...prev, { value: code, label: code.toUpperCase() }]
    );
  };

  const ensureGuidelineOption = (code: string) => {
    if (!code) return;
    setGuidelineOptions((prev) =>
      prev.some((option) => option.value === code)
        ? prev
        : [...prev, { value: code, label: code.toUpperCase() }]
    );
  };

  const resetResourceSelections = () => {
    setSelectedGlossaries([]);
    setSelectedMemories([]);
    setSelectedGuidelines([]);
  };

  const handleClientChange = (value: string | undefined) => {
    const normalizedValue = value || undefined;
    setSelectedClient(normalizedValue);
    setSelectedDirection(undefined);
    setSelectedDomain(undefined);
    resetResourceSelections();
    setSelectedSpeed('medium');

    if (!normalizedValue) {
      setDirectionOptions([]);
      setDirectionDomainMap({});
      return;
    }

    const client = clients.find((c) => c.client_code === normalizedValue);
    const map: Record<string, string> = {};

    if (client?.preferences) {
      Object.entries(client.preferences).forEach(([domain, directions]) => {
        if (directions && typeof directions === 'object') {
          Object.keys(directions).forEach((directionKey) => {
            const normalizedDirection = normalizeString(directionKey);
            if (normalizedDirection && !map[normalizedDirection]) {
              map[normalizedDirection] = domain;
            }
          });
        }
      });
    }

    setDirectionDomainMap(map);
    const options = Object.keys(map).map((direction) => ({
      value: direction,
      label: direction.toUpperCase(),
    }));
    setDirectionOptions(options);
  };

  const loadClientPreferences = async (clientCode: string, direction: string, domain: string) => {
    setPreferencesLoading(true);
    try {
      const response = await axios.get(
        `${getEndpoint('BACKEND')}/recuperaPreferenze/${clientCode}/${direction}/${domain}`
      );

      const prefs: PreferencesResponse = response.data || {};

      const normalizedGlossaries = normalizeValueList(prefs.used_glossaries);
      normalizedGlossaries.forEach(ensureGlossaryOption);
      setSelectedGlossaries(normalizedGlossaries);

      const normalizedMemories = normalizeValueList(prefs.used_memories);
      normalizedMemories.forEach(ensureMemoryOption);
      setSelectedMemories(normalizedMemories);

      const normalizedGuidelines = normalizeValueList(prefs.used_guidelines);
      normalizedGuidelines.forEach(ensureGuidelineOption);
      setSelectedGuidelines(normalizedGuidelines);
    } catch (error) {
      console.error('Error fetching client preferences:', error);
      message.error('Failed to load client preferences.');
      resetResourceSelections();
    } finally {
      setPreferencesLoading(false);
    }
  };

  const handleDirectionChange = (value: string | undefined) => {
    const normalizedValue = value || undefined;
    setSelectedDirection(normalizedValue);
    resetResourceSelections();

    if (!selectedClient || !normalizedValue) {
      setSelectedDomain(undefined);
      return;
    }

    const domain = directionDomainMap[normalizedValue] || 'generic';
    setSelectedDomain(domain);
    loadClientPreferences(selectedClient, normalizedValue, domain);
  };

  const isResourceDisabled = !selectedClient || !selectedDirection;

  const clientOptions: OptionItem[] = clients.map((client) => ({
    value: client.client_code,
    label: client.client_name || client.client_code.toUpperCase(),
  }));

  const handleTranslate = async () => {
    if (!selectedClient) {
      message.error('Please select a client.');
      return;
    }
    if (!selectedDirection) {
      message.error('Please select a direction.');
      return;
    }
    if (!textToTranslate.trim()) {
      message.error('Please enter text to translate.');
      return;
    }

    setLoading(true);
    setTranslatedText('');

    try {
      const [sourceLang, targetLang] = selectedDirection.split('-');

      const payload = {
        SourceText: textToTranslate,
        glossary: selectedGlossaries,
        memory: selectedMemories,
        guideline: selectedGuidelines,
        SpecialInstructions: additionalInstructions,
        ClientCode: selectedClient,
        Direction: selectedDirection,
        Domain: selectedDomain,
        Speed: selectedSpeed,
      };

      const response = await axios.post(
        `${getEndpoint('TRANSLATE')}/translateSentence/${sourceLang}/${targetLang}/patent/`,
        payload
      );

      if (response.data && response.data.mode === 'sync') {
        setTranslatedText(response.data.translation_result?.translation_details?.response || '');
      } else if (response.data && response.data.mode === 'async' && response.data.task_id) {
        const taskId = response.data.task_id;
        let pollCount = 0;
        let status = '';
        let resultData: { result?: { translation_details?: { response?: string; reviewed_text?: string } } } | null =
          null;

        while (pollCount < 240) {
          await new Promise((res) => setTimeout(res, 1000));
          const statusResp = await axios.get(`${getEndpoint('BACKEND')}/getTranslationStatus/${taskId}/`);

          if (statusResp.data) {
            status = statusResp.data.status;
            if (status === 'completed' && statusResp.data.result) {
              resultData = statusResp.data;
              break;
            } else if (status === 'error') {
              message.error('Translation failed.');
              setLoading(false);
              return;
            }
          }
          pollCount++;
        }

        if (resultData?.result?.translation_details?.response) {
          setTranslatedText(resultData.result.translation_details.response);
        } else {
          message.error('Translation timed out or invalid response.');
        }
      } else {
        message.error('Failed to submit translation task.');
      }
    } catch (error) {
      console.error('Error translating text:', error);
      message.error('Failed to translate text.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h2>Test Patren v2 Translation</h2>
      <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ marginRight: 16, marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Client</div>
          <Select
            placeholder="Select Client"
            style={{ width: 220 }}
            value={selectedClient}
            onChange={(value) => handleClientChange(value as string | undefined)}
            allowClear
            showSearch
            optionFilterProp="label"
            loading={clientsLoading}
            options={clientOptions}
          />
        </div>
        <div style={{ marginRight: 16, marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Direction</div>
          <Select
            placeholder="Select Direction"
            style={{ width: 200 }}
            value={selectedDirection}
            onChange={(value) => handleDirectionChange(value as string | undefined)}
            allowClear
            showSearch
            optionFilterProp="label"
            disabled={!selectedClient}
            loading={clientsLoading}
            options={directionOptions}
          />
        </div>
        <div style={{ marginRight: 16, marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Glossary</div>
          <Select
            mode="multiple"
            placeholder="Select Glossaries"
            style={{ width: 240 }}
            value={selectedGlossaries}
            onChange={(values) => setSelectedGlossaries(values as string[])}
            disabled={isResourceDisabled}
            loading={preferencesLoading}
            options={glossaryOptions}
            optionFilterProp="label"
            maxTagCount="responsive"
            allowClear
          />
        </div>
        <div style={{ marginRight: 16, marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Memory</div>
          <Select
            mode="multiple"
            placeholder="Select Memories"
            style={{ width: 240 }}
            value={selectedMemories}
            onChange={(values) => setSelectedMemories(values as string[])}
            disabled={isResourceDisabled}
            loading={preferencesLoading}
            options={memoryOptions}
            optionFilterProp="label"
            maxTagCount="responsive"
            allowClear
          />
        </div>
        <div style={{ marginRight: 16, marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Guideline</div>
          <Select
            mode="multiple"
            placeholder="Select Guidelines"
            style={{ width: 240 }}
            value={selectedGuidelines}
            onChange={(values) => setSelectedGuidelines(values as string[])}
            disabled={isResourceDisabled}
            loading={preferencesLoading}
            options={guidelineOptions}
            optionFilterProp="label"
            maxTagCount="responsive"
            allowClear
          />
        </div>
        <div style={{ marginRight: 16, marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Velocità</div>
          <Select
            placeholder="Select speed"
            style={{ width: 240 }}
            value={selectedSpeed}
            onChange={(value) => setSelectedSpeed(value as string)}
            options={speedOptions}
          />
        </div>
        {preferencesLoading && <Spin size="small" style={{ marginBottom: 16 }} />}
      </div>
      <TextArea
        placeholder="Enter additional instructions"
        value={additionalInstructions}
        onChange={(e) => setAdditionalInstructions(e.target.value)}
        rows={4}
        style={{ marginBottom: 16 }}
        autoSize={{ minRows: 2, maxRows: 4 }}
      />
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Text to Translate</div>
        <ReactQuill
          value={textToTranslate}
          onChange={setTextToTranslate}
          style={{ marginBottom: 16, backgroundColor: 'white' }}
        />
      </div>
      <Button type="primary" onClick={handleTranslate} disabled={loading} style={{ marginBottom: 16 }}>
        Translate
      </Button>
      {loading && <Spin style={{ marginLeft: 16 }} />}
      {translatedText && (
        <div>
          <div style={{ marginBottom: 8, fontWeight: 'bold' }}>Translation</div>
          <ReactQuill
            placeholder="Translated text"
            theme="snow"
            value={translatedText}
            readOnly
            style={{ marginTop: 16 }}
          />
        </div>
      )}
    </div>
  );
};

export default TestTranslation;
  const speedOptions: OptionItem[] = [
    { value: 'high', label: 'lento e violento' },
    { value: 'medium', label: 'medio e con poco tedio' },
    { value: 'low', label: 'veloce e sorprendente' },
  ];
